import { useContext, FC } from "react";
// Libraries
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
// Utils
import {
  LOGIN,
  FORGOT_PASSWORD,
  INVITE,
  RESET_PASSWORD,
  NEW_PASSWORD,
  TRADING,
  SSO,
  BU_ID,
  DASHBOARD,
  ANIMALS,
  BLOCKCHAIN,
  SUBSCRIPTION_DETAILS,
} from "constants/Routes";
import { useHasFeature } from "hooks";
// Pages
import SignIn from "pages/Auth/SignIn";
import ForgotPass from "pages/Auth/ForgotPass";
import NewPassword from "pages/Auth/ForgotPass/NewPassword";
import ResetPasswordRequestSent from "pages/Auth/ForgotPass/ResetPasswordRequestSent";
import { Dashboard } from "pages/Dashboard";
import { Subscription } from "subscriptions/pages/Subscription";
import InvitedUserScreen from "pages/Settings/UserManagement/InvitedUserScreen";
import SisenseSSO from "pages/Reports/SisenseSSO";
import BlockChain from "pages/BlockChain";
import { getAppVersionFromLocalStorage, getTokenFromLocalStorage } from "helpers/storage";
import { AppContext, persistVersion } from "config/appProvider";
import { FEATURES } from "constants/Features";
import { hasFeature } from "helpers/general";
import { BU_TYPES } from "trading/constants";
import GoogleTagManagerWatcher from "App/GoogleTagManagerWatcher";
import FathomWatcher from "App/FathomWatcher";
import { BusinessUnitHandler } from "businessUnit/components/BusinessUnitHandler";

export const Routes: FC = () => {
  const {
    mainAppState: { token, selectedBUType, featuresList },
  } = useContext(AppContext);

  const tokenFromLocalStorage = getTokenFromLocalStorage();

  const isShowHomePage: boolean = hasFeature(FEATURES.SHOW_HOME_PAGE, featuresList);

  const isProcessor: boolean = selectedBUType === BU_TYPES.processor;
  const isShowBlockchain: boolean = useHasFeature("BLOCKCHAIN");
  const pathToRedirect: string = isProcessor ? TRADING : ANIMALS;

  const isAuthenticated =
    token &&
    token.length > 0 &&
    tokenFromLocalStorage &&
    tokenFromLocalStorage.length > 0 &&
    persistVersion === getAppVersionFromLocalStorage();

  return (
    <Router>
      <GoogleTagManagerWatcher />
      <BusinessUnitHandler />
      <FathomWatcher />
      <Switch>
        {isAuthenticated ? (
          <Switch>
            <Route path={`${BU_ID}${SUBSCRIPTION_DETAILS}`} component={Subscription} />
            <Route path={`${BU_ID}/system/:sectionName/:subSectionName?`} component={Dashboard} />
            <Route path={`${LOGIN}${SSO}`} exact component={SisenseSSO} />
            {/* BlockChain Route */}
            {isShowBlockchain && <Route exact path={BLOCKCHAIN} component={BlockChain} />}
            <Redirect to={isShowHomePage ? DASHBOARD : pathToRedirect} />
          </Switch>
        ) : (
          <Switch>
            <Route exact path={`${LOGIN}${SSO}?`} component={SignIn} />
            {/*
            We want users to only sign up through mobile. We have
            therefore removed the web sign-up form.

            I have left the sign-up code commented out for now
            in case we want to reimplement it at a later point.
            I think web sign up might be useful in the future
            considering we only can do payments through Stripe
            on the web app.

            Ben Busch 07-12-2022.

            <Route exact path={SIGN_UP} component={SignUp} />
            <Route exact path={SIGN_UP_DETAILS} component={SignUpDetails} />
            <Route exact path={SIGN_UP_SUCCESS} component={SignUpSuccess} />
            <Route exact path={SIGN_UP_FAILURE} component={SignUpFailure} />
            */}
            <Route exact path={FORGOT_PASSWORD} component={ForgotPass} />
            <Route exact path={INVITE} component={InvitedUserScreen} />
            <Route exact path={RESET_PASSWORD} component={ResetPasswordRequestSent} />
            <Route exact path={`${NEW_PASSWORD}/:token`} component={NewPassword} />
            <Route exact path={`${BU_ID}${SUBSCRIPTION_DETAILS}`} component={SignIn} />
            <Redirect to={`${LOGIN}`} />
          </Switch>
        )}
      </Switch>
    </Router>
  );
};
